import { useState, useEffect } from 'react';
import { View, StyleSheet, ViewStyle, Platform, ScrollView, Linking } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Modal, Portal, Button, Text, useTheme, IconButton } from 'react-native-paper';
import i18n from '../lib/i18n';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { createTrialSubscription, syncSubscription, confirmPayment } from '../api/apiService';
import { API_BASE } from '../api/config';
import { clearStripeLoaderCache, getStripePromiseWithRetry } from '@/src/lib/stripeLoader';

// Stripe publishable key fallback - this should be passed as a prop from the parent
const STRIPE_PUBLISHABLE_KEY = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || 'pk_test_51RWIRNH2XrByySGIVg3xqGs1t9gHur1HD5FyalpJWz32f2VIdybZfBokm7YLgsjUN4afVFpHvzlH31JtABzwIKNF00x8mZ2PA7';
const SUPPORT_EMAIL = 'suporte@alldrivers.pt';

type PaymentErrorSource = 'internal' | 'external';
type PaymentFlowError = Error & { paymentErrorSource: PaymentErrorSource };

const createPaymentFlowError = (message: string, source: PaymentErrorSource): PaymentFlowError =>
  Object.assign(new Error(message), { paymentErrorSource: source });

const getUsefulResponseMessage = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') return null;

  for (const field of ['message', 'detail', 'error'] as const) {
    const value = (data as Record<string, unknown>)[field];
    if (typeof value === 'string' && value.trim()) return value;
  }

  return null;
};

const getUsefulStripeMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
};

const isPaymentFlowError = (error: unknown): error is PaymentFlowError =>
  Boolean(
    error &&
    typeof error === 'object' &&
    'paymentErrorSource' in error &&
    ((error as { paymentErrorSource?: unknown }).paymentErrorSource === 'internal' ||
      (error as { paymentErrorSource?: unknown }).paymentErrorSource === 'external'),
  );

// Map app locale to Stripe supported locales
const STRIPE_SUPPORTED_LOCALES = [
  'auto',
  'da',
  'de',
  'en',
  'es',
  'fi',
  'fr',
  'it',
  'ja',
  'nb',
  'nl',
  'pl',
  'pt',
  'pt-BR',
  'sv',
  'zh',
] as const;

type StripeLocale = (typeof STRIPE_SUPPORTED_LOCALES)[number];

const toStripeLocale = (lng?: string): StripeLocale | undefined => {
  const lang = (lng || (typeof navigator !== 'undefined' ? navigator.language : ''))
    .replace('_', '-');
  if (!lang) return undefined;
  if (STRIPE_SUPPORTED_LOCALES.includes(lang as StripeLocale)) {
    return lang as StripeLocale;
  }
  if (lang.startsWith('pt')) return 'pt';
  if (lang.startsWith('en')) return 'en';
  if (lang.startsWith('es')) return 'es';
  if (lang.startsWith('fr')) return 'fr';
  return 'auto';
};

const createStripePromise = () => getStripePromiseWithRetry(STRIPE_PUBLISHABLE_KEY, {
  locale: toStripeLocale((i18n as any)?.language),
});

type SuccessCallback = () => void | Promise<void>;

type HandlePaymentWebProps = {
  visible: boolean;
  onDismiss: () => void;
  clientSecret: string;
  priceId?: string;
  quantity?: number;
  isTrial?: boolean;
  subscriptionId?: string;
  onSuccess?: SuccessCallback;
};

const consumeSuccessCallback = async (onSuccess?: SuccessCallback): Promise<boolean> => {
  if (!onSuccess) return false;
  try {
    await onSuccess();
  } catch (error) {
    console.error('Error completing payment success callback:', error);
    return false;
  }
  return true;
};

function WebPaymentForm({ onCancel, clientSecret, priceId = 'price_monthly', quantity = 1, isTrial = true, subscriptionId, onSuccess }: {
  onCancel: () => void;
  clientSecret: string;
  priceId?: string;
  quantity?: number;
  isTrial?: boolean;
  subscriptionId?: string;
  onSuccess?: SuccessCallback;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [terminalError, setTerminalError] = useState(false);
  const { t } = useTranslation('onboarding');

  // Extract intent ID from client secret
  const intentId = clientSecret.split('_secret_')[0];

  // Store plan details in sessionStorage when component mounts
  useEffect(() => {
    if (typeof window !== 'undefined' && priceId && quantity) {
      const planDetails = {
        price_id: priceId,
        quantity: quantity,
        subscription_id: subscriptionId,
        is_trial: isTrial
      };
      sessionStorage.setItem('selectedPlan', JSON.stringify(planDetails));
    }
  }, [priceId, quantity, subscriptionId, isTrial]);

  const finalizeSubscription = async (setupIntentId: string) => {
    try {
      // Get stored plan details from sessionStorage
      const storedPlan = sessionStorage.getItem('selectedPlan');
      if (!storedPlan) {
        throw createPaymentFlowError(t('paymentErrors.noStoredPlan'), 'internal');
      }

      const { price_id, quantity } = JSON.parse(storedPlan);

      const payload = {
        setup_intent_id: setupIntentId,
        price_id: price_id,
        quantity: quantity
      };

      const resp = await createTrialSubscription(payload);

      if (!resp.ok) {
        const responseMessage = getUsefulResponseMessage(resp.data);
        throw createPaymentFlowError(
          responseMessage ?? t('paymentErrors.trialSubscriptionFailed'),
          responseMessage ? 'external' : 'internal',
        );
      }

      // Clear stored data after successful subscription
      sessionStorage.removeItem('selectedPlan');

      const callbackCompleted = await consumeSuccessCallback(onSuccess);
      setLoading(false);
      if (callbackCompleted) {
        return;
      }
      if (onSuccess) {
        setError(t('paymentError'));
        setTerminalError(true);
        return;
      }
      window.location.href = `${window.location.origin}/dashboard/home`;
    } catch (error: any) {
      console.error('Error finalizing subscription:', error);
      throw error;
    }
  };

  const confirmPaymentLocal = async (paymentIntentId: string) => {
    try {
      if (!subscriptionId) {
        throw createPaymentFlowError(t('paymentErrors.subscriptionIdMissing'), 'internal');
      }

      // Immediately confirm payment and update subscription status
      const response = await confirmPayment(subscriptionId, paymentIntentId);

      if (!response.ok) {
        const responseMessage = getUsefulResponseMessage(response.data);
        throw createPaymentFlowError(
          responseMessage ?? t('paymentErrors.confirmPaymentFailed'),
          responseMessage ? 'external' : 'internal',
        );
      }

      // Success!
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.removeItem('selectedPlan');
      }
      
      const callbackCompleted = await consumeSuccessCallback(onSuccess);
      setLoading(false);
      if (callbackCompleted) {
        return;
      }
      if (onSuccess) {
        setError(t('paymentError'));
        setTerminalError(true);
        return;
      }
      if (typeof window !== 'undefined') {
        window.location.href = `${window.location.origin}/dashboard/home`;
      }
    } catch (error: any) {
      console.error('Error confirming payment:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (loading || terminalError || !stripe || !elements) return;
    setLoading(true);
    setError(null);
    try {
      // Construct return URL to backend payment-redirect endpoint
      // Use API_BASE to ensure we redirect to the correct backend URL (e.g., localhost:8000, not 8081)
      // Pass return_url so backend knows where to redirect after processing
      const backendRedirectUrl = `${API_BASE}/api/subscriptions/payment-redirect/`;
      const params = new URLSearchParams();
      
      // Use the correct parameter name based on whether this is a trial or not
      const intentParamName = isTrial ? 'setup_intent' : 'payment_intent';
      const secretParamName = isTrial ? 'setup_intent_client_secret' : 'payment_intent_client_secret';
      
      params.append(intentParamName, intentId);
      params.append(secretParamName, clientSecret);
      // Provide plan context so backend can create subscription on redirect if needed
      if (priceId) params.append('price_id', String(priceId));
      if (typeof quantity === 'number') params.append('quantity', String(quantity));
      params.append('is_trial', isTrial ? 'true' : 'false');
      
      // Pass the origin so backend can redirect back to same origin (localhost vs IP)
      if (typeof window !== 'undefined') {
        params.append('return_url', `${window.location.origin}/dashboard/home`);
      }

      const returnUrl = `${backendRedirectUrl}?${params.toString()}`;

      if (isTrial) {
        // Handle trial setup with SetupIntent
        try {
          const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: {
              return_url: returnUrl,
            },
            redirect: 'if_required',
          });

          if (error) {
            console.error('WebPaymentForm: Setup confirmation error:', error);
            throw createPaymentFlowError(getUsefulStripeMessage(error, t('paymentError')), 'external');
          }

          // If we get here and setupIntent is successful, create trial subscription
          if (setupIntent && setupIntent.status === 'succeeded') {
            await finalizeSubscription(setupIntent.id);
            return;
          }
        } catch (setupError) {
          console.error('WebPaymentForm: Error with setup:', setupError);
          throw setupError;
        }

        // Fallback to redirect flow for setup (redirect to backend)
        const fallbackParams = new URLSearchParams();
        fallbackParams.append('setup_intent', intentId);
        fallbackParams.append('setup_intent_client_secret', clientSecret);
        if (priceId) fallbackParams.append('price_id', String(priceId));
        if (typeof quantity === 'number') fallbackParams.append('quantity', String(quantity));
        fallbackParams.append('is_trial', isTrial ? 'true' : 'false');
        if (typeof window !== 'undefined') {
          fallbackParams.append('return_url', `${window.location.origin}/dashboard/home`);
        }
        const fallbackReturnUrl = `${API_BASE}/api/subscriptions/payment-redirect/?${fallbackParams.toString()}`;

        const { error: redirectError } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: fallbackReturnUrl,
          },
          redirect: 'always',
        });

        if (redirectError) {
          console.error('WebPaymentForm: Redirect setup error:', redirectError);
          throw redirectError;
        }
      } else {
        // Handle direct purchase with PaymentIntent
        try {
          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: returnUrl,
            },
            redirect: 'if_required',
          });

          if (error) {
            console.error('WebPaymentForm: Payment confirmation error:', error);
            throw createPaymentFlowError(getUsefulStripeMessage(error, t('paymentError')), 'external');
          }

          // If we get here and paymentIntent is successful or requires action
          if (paymentIntent) {
            if (paymentIntent.status === 'succeeded') {
              // For successful payments with subscription ID, confirm payment immediately
              if (subscriptionId) {
                await confirmPaymentLocal(paymentIntent.id);
                return;
              } else {
                // For successful payments without subscription ID (rare case), finalize as trial
                await finalizeSubscription(paymentIntent.id);
                return;
              }
            } else if (paymentIntent.status === 'requires_action') {
              // For payments requiring additional action, let Stripe handle it
              return;
            } else if (paymentIntent.status === 'processing') {
              // Payment is processing, show message and wait
              
              // For processing payments with subscription ID, confirm payment
              if (subscriptionId) {
                try {
                  await confirmPaymentLocal(paymentIntent.id);
                  return;
                } catch (syncError) {
                  console.error('Error syncing processing payment:', syncError);
                  setError(t('paymentProcessing'));
                  setLoading(false);
                  return;
                }
              } else {
                setError(t('paymentProcessing'));
                setLoading(false);
                return;
              }
            } else if (subscriptionId && 
                      (paymentIntent.status === 'requires_payment_method' || 
                       paymentIntent.status === 'requires_confirmation' || 
                       paymentIntent.status === 'requires_capture')) {
              // For incomplete payments with existing subscription, confirm payment
              await confirmPaymentLocal(paymentIntent.id);
              return;
            }
          }
        } catch (paymentError) {
          console.error('WebPaymentForm: Error with payment:', paymentError);
          throw paymentError;
        }

        // Fallback to redirect flow for payment (redirect to backend)
        const fallbackParams = new URLSearchParams();
        fallbackParams.append('payment_intent', intentId);
        fallbackParams.append('payment_intent_client_secret', clientSecret);
        if (priceId) fallbackParams.append('price_id', String(priceId));
        if (typeof quantity === 'number') fallbackParams.append('quantity', String(quantity));
        fallbackParams.append('is_trial', isTrial ? 'true' : 'false');
        if (typeof window !== 'undefined') {
          fallbackParams.append('return_url', `${window.location.origin}/dashboard/home`);
        }
        const fallbackReturnUrl = `${API_BASE}/api/subscriptions/payment-redirect/?${fallbackParams.toString()}`;

        const { error: redirectError } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: fallbackReturnUrl,
          },
          redirect: 'always',
        });

        if (redirectError) {
          console.error('WebPaymentForm: Redirect payment error:', redirectError);
          throw createPaymentFlowError(getUsefulStripeMessage(redirectError, t('paymentError')), 'external');
        }
      }
    } catch (e: any) {
      console.error('WebPaymentForm: Error in handleSubmit:', e);
      const errorMessage = isPaymentFlowError(e) && e.message.trim()
        ? e.message
        : t('paymentError');
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <View style={styles.formContainer} testID="stripe-payment-form">
      <ScrollView style={styles.formContent}>
        <PaymentElement onReady={() => { }} options={{ layout: { type: 'accordion', defaultCollapsed: false } }} />
        {error && <Text style={{ color: 'red', marginTop: 12 }}>{error}</Text>}
      </ScrollView>
      <View style={[styles.actions, { borderTopColor: theme.colors.outline }]}>
        <Button onPress={onCancel}>{t('cancel')}</Button>
        <Button mode="contained" onPress={handleSubmit} loading={loading} disabled={loading || terminalError || !stripe || !elements} testID="stripe-pay-now">
          {loading
            ? t('trialProcessing')
            : isTrial
              ? t('trialStartButton')
              : t('completePurchase')}
        </Button>
      </View>
    </View>
  );
}

export function HandlePayment({ visible, onDismiss, clientSecret, priceId = 'price_monthly', quantity = 1, isTrial = true, subscriptionId, onSuccess }: HandlePaymentWebProps) {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const [stripePromise, setStripePromise] = useState(createStripePromise);
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let mounted = true;

    setStripeLoadFailed(false);
    stripePromise
      .then((loadedStripe) => {
        if (!mounted) return;
        setStripeLoadFailed(!loadedStripe);
      })
      .catch(() => {
        if (!mounted) return;
        setStripeLoadFailed(true);
      });

    return () => {
      mounted = false;
    };
  }, [stripePromise, visible]);

  const handleRetryStripeLoad = () => {
    clearStripeLoaderCache();
    setStripeLoadFailed(false);
    setStripePromise(createStripePromise());
  };

  const handleContactSupport = () => {
    const subject = encodeURIComponent(t('supportEmailSubject'));
    Linking.openURL(`mailto:${SUPPORT_EMAIL}?subject=${subject}`).catch(() => {
      // Mail client unavailable — the visible support email remains as fallback.
    });
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        dismissable={false}
        testID="buy-license-modal"
        contentContainerStyle={[styles.container, { backgroundColor: theme.colors.background } as ViewStyle]}
      >
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.header}>
            <Text variant="headlineMedium" style={{ color: theme.colors.primary }}>
              {isTrial 
                ? t('trialModalTitle')
                : t('paymentModalTitle')}
            </Text>
            <IconButton
              icon="close"
              size={24}
              onPress={onDismiss}
              iconColor={theme.colors.primary}
              testID="payment-dismiss"
            />
          </View>
          {stripeLoadFailed ? (
            <View style={styles.stripeFallback} testID="stripe-loader-error">
              <Text style={[styles.stripeFallbackTitle, { color: theme.colors.error }]}>
                {t('stripeLoadErrorTitle')}
              </Text>
              <Text style={styles.stripeFallbackMessage}>
                {t('stripeLoadErrorMessage')}
              </Text>
              <Text style={styles.supportEmail}>{SUPPORT_EMAIL}</Text>
              <View style={styles.stripeFallbackActions}>
                <Button mode="outlined" onPress={handleRetryStripeLoad} testID="stripe-loader-retry">
                  {t('tryAgain')}
                </Button>
                <Button mode="contained" onPress={handleContactSupport} testID="stripe-loader-support">
                  {t('contactSupport')}
                </Button>
              </View>
            </View>
          ) : (
            <Elements stripe={stripePromise} options={{
              clientSecret,
              appearance: { theme: 'stripe' },
              loader: 'auto'
            }}>
              <WebPaymentForm
                onCancel={onDismiss}
                clientSecret={clientSecret}
                priceId={priceId}
                quantity={quantity}
                isTrial={isTrial}
                subscriptionId={subscriptionId}
                onSuccess={onSuccess}
              />
            </Elements>
          )}
        </SafeAreaView>
      </Modal>
    </Portal>
  );
}

const styles = StyleSheet.create({
  container: {
    ...Platform.select({
      web: {
        maxWidth: 600,
        width: '90%',
        maxHeight: '90%',
        height: 'auto',
        margin: 'auto',
        borderRadius: 8,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }
    }),
  },
  safeArea: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  formContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  formContent: {
    flex: 1,
    paddingRight: 8,
    paddingBottom: 16,
  },
  stripeFallback: {
    padding: 24,
    gap: 12,
    alignItems: 'center',
  },
  stripeFallbackTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
  },
  stripeFallbackMessage: {
    textAlign: 'center',
    lineHeight: 20,
  },
  supportEmail: {
    fontWeight: '600',
  },
  stripeFallbackActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    padding: 16,
    borderTopWidth: 1,
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
  },
  startButton: {
    paddingVertical: 8,
  },
});
